import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MAX_SCOPE_IDS, normalizeSearch, SCOPE_CLIENT, SCOPE_LABEL, SCOPE_LINE} from '../rulesModel';

function toggleId(ids, id) {
    return ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];
}

/**
 * Sobre qué aplica la regla: clientes, líneas o etiquetas, del roster de `options` (sólo lo que el
 * usuario puede ver: lo filtra el backend con `ClientAccessService`). Elección múltiple, con el tope
 * del backend (`limits.max_scope_ids`) a la vista.
 *
 * `staleIds`: los elegidos que ya no están en el roster (una línea terminada, un cliente desactivado).
 * No se ven en la lista, así que se nombran con su salida: guardarlos de nuevo da `INVALID_SCOPE`.
 */
export function ScopePicker({type, ids, options, onChange, max = MAX_SCOPE_IDS, staleIds = []}) {
    const {t} = useTranslation();
    const [query, setQuery] = useState('');
    const needle = normalizeSearch(query);
    const atMax = ids.length >= max;
    const stale = staleIds.filter((id) => ids.includes(id));

    const clients = useMemo(() => (options && options.clients) || [], [options]);
    const labels = useMemo(() => (options && options.labels) || [], [options]);

    const checkbox = (id, label, extra, unavailable = false) => {
        const checked = ids.includes(id);
        const inputId = `alc-scope-${type}-${id}`;
        return (
            <li key={id} className={`alc-pick__item${unavailable ? ' -unavailable' : ''}`}>
                <input id={inputId} type="checkbox" checked={checked} disabled={!checked && (atMax || unavailable)}
                       onChange={() => onChange(toggleId(ids, id))}/>
                <label htmlFor={inputId}>
                    <span className="alc-pick__name">{label}</span>
                    {extra && <span className="alc-pick__extra">{extra}</span>}
                </label>
            </li>
        );
    };

    const renderClients = () => {
        const rows = clients.filter((client) => !needle || normalizeSearch(client.name).includes(needle));
        if (!rows.length) return <p className="alc-pick__empty">{t('alerts_center.builder.pick_none')}</p>;
        return (
            <ul className="alc-pick__list">
                {rows.map((client) => checkbox(client.id, client.name,
                    t('alerts_center.builder.lines_count', {count: (client.lines || []).length})))}
            </ul>
        );
    };

    const renderLines = () => {
        const groups = clients.map((client) => {
            const clientMatches = !needle || normalizeSearch(client.name).includes(needle);
            const lines = (client.lines || []).filter((line) => clientMatches || normalizeSearch(line.name).includes(needle));
            return {client, lines};
        }).filter((group) => group.lines.length > 0);
        if (!groups.length) return <p className="alc-pick__empty">{t('alerts_center.builder.pick_none')}</p>;
        return groups.map(({client, lines}) => (
            <div key={client.id} className="alc-pick__group">
                <div className="alc-pick__group-title">{client.name}</div>
                <ul className="alc-pick__list">
                    {lines.map((line) => checkbox(line.id, line.name, line.currency || null))}
                </ul>
            </div>
        ));
    };

    const renderLabels = () => {
        const rows = labels.filter((label) => !needle || normalizeSearch(label.name).includes(needle));
        if (!rows.length) return <p className="alc-pick__empty">{t('alerts_center.builder.pick_no_labels')}</p>;
        // Una etiqueta sin ninguna línea que quien mira vea no alcanza nada: se ve, deshabilitada y con
        // el motivo (C-ac-fe-centro-11). Si ya estaba elegida, se puede sacar.
        return (
            <ul className="alc-pick__list">
                {rows.map((label) => {
                    const empty = label.lines_count === 0;
                    const extra = typeof label.lines_count !== 'number' ? null
                        : (empty ? t('alerts_center.builder.pick_label_empty')
                            : t('alerts_center.builder.lines_count', {count: label.lines_count}));
                    return checkbox(label.id, label.name, extra, empty);
                })}
            </ul>
        );
    };

    return (
        <div className="alc-pick" data-scope={type}>
            <input type="search" className="alc-pick__search" data-autofocus value={query}
                   placeholder={t('alerts_center.builder.pick_search')}
                   aria-label={t('alerts_center.builder.pick_search')}
                   onChange={(event) => setQuery(event.target.value)}/>
            <div className="alc-pick__scroll">
                {type === SCOPE_CLIENT && renderClients()}
                {type === SCOPE_LINE && renderLines()}
                {type === SCOPE_LABEL && renderLabels()}
            </div>
            {stale.length > 0 && (
                <div className="alc-pick__stale" data-testid="alc-pick-stale">
                    <span>{t('alerts_center.builder.pick_stale', {count: stale.length})}</span>
                    <button type="button" className="alc-link -button"
                            onClick={() => onChange(ids.filter((id) => !stale.includes(id)))}>
                        {t('alerts_center.builder.pick_stale_remove')}
                    </button>
                </div>
            )}
            <div className="alc-pick__foot">
                <span>{t('alerts_center.builder.picked', {count: ids.length, max})}</span>
                {ids.length > 0 && (
                    <button type="button" className="alc-link -button" onClick={() => onChange([])}>
                        {t('alerts_center.builder.pick_clear')}
                    </button>
                )}
            </div>
        </div>
    );
}
